<template>
    <section class="improvements-grid">
        <error-handler class="improvements-grid__errors" />
        <hh-msg-display v-if="message"
                        class="improvements-grid__message"
                        type="info"
                        :message="message" />
        <loading-animation v-if="loading" class="improvements-grid__loading">
            Calculating Improvements&hellip;
        </loading-animation>
        <ul v-else
            class="improvements-grid__data">
            <li v-for="item in filteredImprovements"
                :key="`imp${item.id}`">
                <improvement-tile :improvement="item"
                                  :last-changed="isLastChanged(item)"
                                  :selected-improvements="currentProject.addedImprovements"
                                  @change="onTileStateChanged"
                                  @viewed="onTileViewed" />
            </li>
            <li v-for="i in gridSpacers"
                :key="`sp${i}`"
                class="improvements-grid__data__spacers" />
        </ul>
        <error-handler v-if="!loading"
                       class="improvements-grid__errors" />
        <hh-msg-display v-if="messageAfterLoad"
                        class="improvements-grid__message"
                        type="info"
                        :message="message" />
    </section>
</template>

<script>

import FilteredDataHandler from './filtered-data-handler'

import ErrorHandler from '@/components/app-core/error-handler'
import ImprovementTile from './improvement-tile'
import LoadingAnimation from '@/components/app-core/loading-animation'

export default {

    mixins: [
        FilteredDataHandler,
    ],

    props: {
        loading: {
            type: Boolean,
            default: true,
        },
    },

    data() {
        return {
            lastChanged: null,
            message: null,
            lastDetailViewed: null,
        }
    },

    computed: {

        gridSpacers() {
            return [ 1, 2, 3, 4, 5 ]
        },

        messageAfterLoad() {
            return this.message !== null && this.message !== '' && !this.loading
        },
    },

    watch: {

        notAppliedImprovements(newValue) {
            this.message = newValue && newValue.length
                ? newValue.length > 1
                    ? `Some improvements are incompatible with other options that you have selected and has been ignored when calculating your savings.`
                    : `${ newValue[0].title } is incompatible with other options that you have selected and has been ignored when calculating your savings.`
                : null
        },
    },

    updated() {
        setTimeout(() => {
            this.lastChanged = null
        }, 2500)
    },

    methods: {

        isLastChanged(item) {
            return item == this.lastChanged
        },

        onTileStateChanged(improvement) {
            this.lastChanged = improvement
        },

        onTileViewed(tileDataRef) {
            this.lastDetailViewed = tileDataRef
            this.$eventbus.$off(this.$appevents.afterModalClose)
            this.$eventbus.$on(this.$appevents.afterModalClose, () => {
                this.$nextTick(() => {
                    this.$logger.logObject({ ref: this.lastDetailViewed }, 'Post close action', 'Modal')
                    this.lastDetailViewed.focus()
                })
            })
        },
    },

    components: {
        ErrorHandler,
        ImprovementTile,
        LoadingAnimation,
    },
}

</script>

<style lang="scss">

// Ensure colours and other visual style elements are added to theme files, not placed in here.
// src\scss\schemes\components\plan-builder\_improvements-grid.scss

$improvements-grid-item-spacing: rem(5px);

.improvements-grid {
    @include elements('errors' 'message') {
        display: block;
        font-size: smaller;
            margin: 0.5rem 1rem;

        & > * {
            font-size: 0.9rem;
        }
    }

    @include element('data') {
        @include unstyle-list;
        display: flex;
        flex-direction: row;
        flex-wrap: wrap;
        align-items: stretch;
        align-content: stretch;
        padding: 0;
        margin-top: $improvements-grid-item-spacing*2;
        margin-bottom: -$improvements-grid-item-spacing*2;

        @include bp-min-small {
            margin-bottom: -$improvements-grid-item-spacing*4;
        }

        > li {
            display: block;
            flex: 1 1 rem(144px);
            margin: 0 $improvements-grid-item-spacing $improvements-grid-item-spacing*2 $improvements-grid-item-spacing;

            @include bp-min-small {
                flex: 1 1 rem(184px);
                margin: 0 $improvements-grid-item-spacing*2 $improvements-grid-item-spacing*4 $improvements-grid-item-spacing*2;
            }
        }

        @include element("spacers") {
            height: 0 !important;
            margin-bottom: 0 !important;
        }
    }
}

</style>
